import { DateRangePicker, type DateRange } from "@nexus/ui";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  CUSTOM_PRESET_ID,
  buildExplorePresets,
  isCatalogPresetId,
  resolvePresetWindow,
  type ExploreDateSelectionFields,
} from "@/features/dashboard/lib/explore-date-presets";
import { formatYmd, parseYmdLocal } from "@/features/dashboard/lib/ymd";

export type ExploreDateSelection = ExploreDateSelectionFields;

export interface ExploreDateRangePickerProps {
  startDate: string;
  endDate: string;
  activePresetId: string;
  onChange: (next: ExploreDateSelection) => void;
  /** Injectable for tests; defaults to now. */
  today?: Date;
  disabled?: boolean;
  triggerClassName?: string;
  id?: string;
}

/**
 * The explore-page date control: preset rail plus calendar.
 *
 * Mode-agnostic on purpose — the caller passes the active explore mode's values,
 * so each mode keeps its own range.
 */
export function ExploreDateRangePicker({
  startDate,
  endDate,
  activePresetId,
  onChange,
  today,
  disabled,
  triggerClassName,
  id,
}: ExploreDateRangePickerProps) {
  const { t } = useTranslation();

  const referenceDate = useMemo(() => today ?? new Date(), [today]);
  const presets = useMemo(
    () => buildExplorePresets(t, referenceDate),
    [t, referenceDate],
  );

  const value = useMemo<DateRange | undefined>(() => {
    const from = parseYmdLocal(startDate ?? "");
    if (from) {
      const to = parseYmdLocal(endDate);
      return { from, to: to ?? undefined };
    }
    // Stored dates start empty so no window is captured at import time. Resolve
    // the active preset for display instead of falling back to a placeholder.
    const resolved = resolvePresetWindow(activePresetId, referenceDate);
    if (!resolved) return undefined;
    return {
      from: parseYmdLocal(resolved.start) ?? undefined,
      to: parseYmdLocal(resolved.end) ?? undefined,
    };
  }, [startDate, endDate, activePresetId, referenceDate]);

  function handleCommit(next: {
    range: DateRange | undefined;
    presetId: string | undefined;
  }) {
    // A preset id with no range means the rail was clicked but the preset could
    // not be resolved; nothing to commit.
    if (next.presetId && isCatalogPresetId(next.presetId)) {
      const window = resolvePresetWindow(next.presetId, referenceDate);
      if (!window) return;
      onChange({
        startDate: window.start,
        endDate: window.end,
        activePresetId: next.presetId,
      });
      return;
    }

    // No preset means the calendar was used. Apply is gated on a complete range,
    // so both ends are present here.
    if (!next.range?.from || !next.range.to) return;
    onChange({
      startDate: formatYmd(next.range.from),
      endDate: formatYmd(next.range.to),
      activePresetId: CUSTOM_PRESET_ID,
    });
  }

  return (
    <DateRangePicker
      id={id}
      value={value}
      onChange={() => undefined}
      onCommit={handleCommit}
      presets={presets}
      activePresetId={activePresetId}
      disabled={disabled}
      triggerClassName={triggerClassName}
      toDate={referenceDate}
      labels={{
        placeholder: t("dashboard.dateRangePicker.placeholder", "Select dates"),
        triggerAria: t("dashboard.dateRangePicker.triggerAria", "Select date range"),
        presetList: t("dashboard.dateRangePicker.presetList", "Date range presets"),
        clear: t("dashboard.dateRangePicker.clear", "Clear"),
        apply: t("dashboard.dateRangePicker.apply", "Apply"),
        custom: t("dashboard.dateRangePicker.custom", "Custom"),
      }}
    />
  );
}
